import type { Component, JSX, VoidComponent } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import { TextButton } from "../button/Button";
import { ColorPicker as ArkColorPicker, parseColor } from "@ark-ui/solid";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import { Portal } from "solid-js/web";
import type { HexColor } from "../../types";
import { createWritableMemo } from "@solid-primitives/memo";
import { keyFromSelector } from "i18next";

const FALLBACK_COLOR = "#000000";
const PREDEFINED_COLORS: { hex: HexColor; label: TranslationKey }[] = [
  { hex: "#DC2626", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_RED) },
  { hex: "#D97706", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_ORANGE) },
  { hex: "#FCD34D", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_YELLOW) },
  { hex: "#548B5F", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_GREEN) },
  { hex: "#1AB0AF", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_TEAL) },
  { hex: "#0284C7", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_BLUE) },
  { hex: "#2563EB", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_ROYAL_BLUE) },
  { hex: "#4F46E5", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_INDIGO) },
  { hex: "#3F5D8B", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_GRAY_BLUE) },
  { hex: "#1F2937", label: keyFromSelector(($) => $.COLOR_PICKER_PREDEFINED_COLORS_BLACK) },
];

export type ColorPickerProps = {
  Trigger: Component<JSX.IntrinsicElements["button"]>;
  value: string;
  onChange: (value: string) => void;
  showOpacity?: boolean;
};

const ColorPicker: VoidComponent<ColorPickerProps> = (props) => {
  const { t } = useTranslation();
  const [open, setOpen] = createSignal(false);
  const [value, setValue] = createWritableMemo(() => {
    try {
      return parseColor(props.value);
      // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return parseColor(FALLBACK_COLOR);
    }
  });
  const showOpacity = createMemo(() => props.showOpacity ?? false);

  return (
    <ArkColorPicker.Root
      format="rgba"
      class="relative"
      lazyMount
      value={value()}
      onValueChange={({ value }) => {
        setValue(value);
      }}
      open={open()}
      onOpenChange={(state) => {
        setOpen(state.open);
      }}
      onInteractOutside={(event) => {
        /**
         * This prevents the modal from closing when the content is pressed.
         * There is a bug in the library with the targets returned from this event aren't correct.
         * This event is only called once when the content is pressed (it shouldn't be called at all).
         * The first time the content is pressed, the target is the body (for some reason).
         */
        if (event.detail.originalEvent.target === document.body) {
          event.preventDefault();
        }
      }}
    >
      <ArkColorPicker.Control>
        <ArkColorPicker.Trigger
          asChild={(triggerProps) => (
            <props.Trigger
              {...triggerProps()}
              aria-label={t(($) => $.COLOR_PICKER_TRIGGER_ARIA_LABEL)}
              aria-expanded={open()}
            />
          )}
        />
      </ArkColorPicker.Control>
      <Portal>
        <ArkColorPicker.Positioner>
          <Show when={open()}>
            <ArkColorPicker.Content
              class="z-50 flex h-108.5 w-78 flex-col rounded-sm bg-surface-default p-4 shadow-elevation-light-3"
              role="dialog"
              aria-label={t(($) => $.COLOR_PICKER_DIALOG_ARIA_LABEL)}
            >
              <ArkColorPicker.Area class="h-43.5 w-full">
                <ArkColorPicker.AreaBackground class="size-full rounded-sm" />
                <ArkColorPicker.AreaThumb
                  class="size-4 rounded-full border border-inverse shadow-elevation-light-2"
                  aria-label={t(($) => $.COLOR_PICKER_SATURATION_BRIGHTNESS_SLIDER_ARIA_LABEL)}
                />
              </ArkColorPicker.Area>
              <div class="mt-2 flex w-full flex-col items-center gap-2">
                <ArkColorPicker.ChannelSlider channel="hue" class="h-3 w-full">
                  <ArkColorPicker.ChannelSliderTrack class="size-full rounded-full" />
                  <ArkColorPicker.ChannelSliderThumb
                    class="size-4 -translate-1/2 rounded-full border-2 border-inverse shadow-elevation-light-2"
                    aria-label={t(($) => $.COLOR_PICKER_HUE_SLIDER_ARIA_LABEL)}
                  />
                </ArkColorPicker.ChannelSlider>

                <Show when={showOpacity()}>
                  <ArkColorPicker.ChannelSlider channel="alpha" class="h-3 w-full">
                    <ArkColorPicker.TransparencyGrid class="rounded-full" />
                    <ArkColorPicker.ChannelSliderTrack class="size-full rounded-full" />
                    <ArkColorPicker.ChannelSliderThumb
                      class="size-4 -translate-1/2 rounded-full border-2 border-inverse shadow-elevation-light-2"
                      aria-label={t(($) => $.COLOR_PICKER_ALPHA_SLIDER_ARIA_LABEL)}
                    />
                  </ArkColorPicker.ChannelSlider>
                </Show>
              </div>
              <div class="mt-2">
                <label for="hexcode" class="mb-1 text-base-semibold text-secondary">
                  {t(($) => $.COLOR_PICKER_HEX_LABEL)}
                </label>
                <div class="flex gap-1">
                  <ArkColorPicker.ChannelInput
                    id="hexcode"
                    channel="hex"
                    class="w-full rounded-sm border border-default bg-surface-default p-2 text-base-medium text-primary"
                    aria-label={t(($) => $.COLOR_PICKER_HEX_INPUT_ARIA_LABEL)}
                  />

                  <Show when={showOpacity()}>
                    <ArkColorPicker.ChannelInput
                      id="opacity"
                      channel="alpha"
                      class="w-16 rounded-sm border border-default bg-surface-default p-2 text-base-medium text-primary"
                      aria-label={t(($) => $.COLOR_PICKER_ALPHA_INPUT_ARIA_LABEL)}
                    />
                  </Show>
                </div>
              </div>
              <div class="mt-4 flex flex-col">
                <span class="mb-1 text-base-semibold text-secondary">
                  {t(($) => $.COLOR_PICKER_PREDEFINED_COLORS_LABEL)}
                </span>
                <ArkColorPicker.SwatchGroup class="flex size-full gap-1">
                  <For each={PREDEFINED_COLORS}>
                    {(color) => (
                      <ArkColorPicker.SwatchTrigger
                        value={color.hex}
                        aria-label={t(($) => $.COLOR_PICKER_PREDEFINED_COLORS_BUTTON_ARIA_LABEL, {
                          color: t(color.label),
                        })}
                      >
                        <ArkColorPicker.Swatch value={color.hex} class="size-6 rounded-sm checked:outline-default" />
                      </ArkColorPicker.SwatchTrigger>
                    )}
                  </For>
                </ArkColorPicker.SwatchGroup>
              </div>
              <div class="mt-4 ml-auto flex grow-0 gap-2">
                <TextButton
                  variant="secondary"
                  size="large"
                  text={t(($) => $.CANCEL_BUTTON)}
                  onClick={() => {
                    setOpen(false);
                  }}
                />
                <ArkColorPicker.Context>
                  {(context) => (
                    <TextButton
                      size="large"
                      text={t(($) => $.SELECT_BUTTON)}
                      onClick={() => {
                        setOpen(false);
                        props.onChange(context().value.toString(showOpacity() ? "hexa" : "hex"));
                      }}
                    />
                  )}
                </ArkColorPicker.Context>
              </div>
            </ArkColorPicker.Content>
          </Show>
        </ArkColorPicker.Positioner>
      </Portal>
      <ArkColorPicker.HiddenInput aria-hidden />
    </ArkColorPicker.Root>
  );
};

export default ColorPicker;
